/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : SelectVehicleSystemDialog.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import SelectZone from "./SelectZone";
import CommonBreadcrumbs from "../../../../components/customComponentsV2/common/Breadcrumbs/CommonBreadcrumbs";
import SelectComponentList from "./SelectComponentList";
import { HandleApiActions, labCar } from "../../../../redux/actions";
import softwareIntersectionAPI from "../../../../redux/actions/softwareIntersectionActions";
import { useDispatch, useSelector } from "react-redux";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CustomCheckBoxWithoutFormControl from "../../../../components/customComponentsV2/common/checkBox/CustomCheckBoxWithoutFormControl";
import { Radio } from "@mui/material";
import {
  checkedIconStyle,
  unCheckedIconStyle,
} from "../../../../components/customComponentsV2/common/radioButton/CustomRadioButtonV2";
import CustomToolTip from "../../../../components/infoButton/CustomToolTip";

function SelectVehicleSystemDialog({
  isZonalProject,
  zoneList = [],
  selectedZone,
  setSelectedZone,
  showZone,
  setShowZone,
  selectedVS,
  setSelectedVS,
  setSelectedZoneName,
  selectedZoneName,
  selectedVC,
  setSelectedVC,
  disabledVSComponents = [],
  selectedLegalMain,
  requireVehicleComponentSelection = true,
  isEditMode = false,
}) {
  const zone = "zone";
  const vs = "vs";
  const vc = "vc";

  const dispatch = useDispatch();

  const [vsModifiedList, setVSModifiedList] = useState([]);
  const [vcModifiedList, setVCModifiedList] = useState([]);
  const [currentViewingVS, setCurrentViewingVS] = useState(null);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [projectID, setProjectID] = useState(null);
  const [variantID, setVariantID] = useState(null);
  const [disabledVCComponents, setDisabledVCComponents] = useState([]);

  const selectedProjectDetails = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails,
  );
  const selectedSingleVariantDetails = useSelector(
    (state) => state.projectConfigReducer.selectedVariantDetails,
  );

  useEffect(() => {
    if (selectedProjectDetails?.project_id) {
      setProjectID(selectedProjectDetails?.project_id);
    }
    if (selectedSingleVariantDetails?.variant_id) {
      setVariantID(selectedSingleVariantDetails?.variant_id);
    }
  }, [selectedProjectDetails, selectedSingleVariantDetails]);

  const modifyVSData = (data) => {
    const modifiedList = data.map((item) => ({
      data: item,
      id: item.id,
      cardDataArray: [
        { key: "Name", value: item.name, keyStyle: { fontWeight: 600 } },
        { key: "Description", value: item.description },
      ],
    }));
    setVSModifiedList(modifiedList);
  };

  const modifyVCData = (data) => {
    const modifiedList = data.map((item) => ({
      data: item,
      id: item.id,
      cardDataArray: [
        { key: "Name", value: item.name, keyStyle: { fontWeight: 600 } },
        { key: "Component Number", value: item.component_number },
      ],
    }));
    setVCModifiedList(modifiedList);
  };

  const getChangedZone = (zone, zoneName) => {
    setSelectedZone(zone);
    setSelectedZoneName(zoneName);
    setVSModifiedList([]); // clear stale list so Select All state resets before new zone data loads
    setShowZone(vs);
  };

  const getEnabledVCList = () =>
    vcModifiedList.filter(
      (item) => !disabledVCComponents.includes(Number(item?.data?.id)),
    );

  const getDisabledVCListForLR = async ({ lrId, vsId }) => {
    if (!lrId || !vsId) {
      setDisabledVCComponents([]);
      return;
    }
    if (!projectID || !variantID) {
      setDisabledVCComponents([]);
      return;
    }
    const resp = await dispatch(
      HandleApiActions({
        ...softwareIntersectionAPI.get_vc_for_legal_requirements,
        params: {
          project_id: Number(projectID),
          variant_id: Number(variantID),
          lr_id: Number(lrId),
          vs_id: Number(vsId),
        },
        show_toast: false,
        ignore_app_loading_spinner: true,
      }),
    );
    if (resp.error) {
      setDisabledVCComponents([]);
      return;
    }
    const vcIds = resp?.data?.[0]?.vc_ids || [];
    const normalizedIds = vcIds
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id));
    setDisabledVCComponents(normalizedIds);
  };

  const updateSelectedVS = (prev, id, vcData) =>
    prev.map((vs) => (vs.id === id ? { ...vs, vcData } : vs));

  // ── Select All helpers (VS mode only, requireVehicleComponentSelection=false) ──

  const enabledVSItems = vsModifiedList
    .map((item) => item.data)
    .filter((data) => !disabledVSComponents.includes(Number(data?.id)));

  const allEnabledSelected =
    enabledVSItems.length > 0 &&
    enabledVSItems.every((data) =>
      (selectedVS || []).some((ele) => ele.id === data.id),
    );

  const someEnabledSelected =
    !allEnabledSelected &&
    enabledVSItems.some((data) =>
      (selectedVS || []).some((ele) => ele.id === data.id),
    );

  const handleSelectAll = (checked) => {
    if (!checked) {
      // Deselect only the items visible in the current list; keep items from other zones
      const currentIds = new Set(enabledVSItems.map((d) => d.id));
      setSelectedVS((prev) =>
        (prev || []).filter((vs) => !currentIds.has(vs.id)),
      );
      return;
    }

    // Add all enabled items not already selected.
    // No VC API fetch here — VCs are only needed when requireVehicleComponentSelection=true,
    // in which case the user navigates into each VS individually via the chevron arrow.
    const toAdd = enabledVSItems.filter(
      (data) => !(selectedVS || []).some((ele) => ele.id === data.id),
    );

    setSelectedVS((prev) => [
      ...(prev || []),
      ...toAdd.map((data) => ({ ...data, vcData: [] })),
    ]);
  };

  return (
    <Box>
      {showZone === zone && (
        <Box>
          <Box sx={{ mb: 1 }}>
            <CommonBreadcrumbs
              color={"black"}
              items={[{ label: "Select Zone" }]}
            />
          </Box>
          <SelectZone
            zoneList={zoneList}
            getChangedZone={getChangedZone}
            selectedZone={selectedZone}
          />
        </Box>
      )}

      {showZone === vs && (
        <Box>
          {/* Breadcrumb + Select All on the same row */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <CommonBreadcrumbs
              color={"black"}
              items={[
                ...(isZonalProject
                  ? [
                    {
                      label: selectedZoneName,
                      onClick: () => setShowZone(zone),
                    },
                  ]
                  : []),
                { label: "Vehicle System" },
              ]}
            />

            {/* Select All — only in VS-multi-select mode */}
            {!requireVehicleComponentSelection &&
              enabledVSItems.length > 0 && (
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                >
                  <CustomCheckBoxWithoutFormControl
                    value={allEnabledSelected}
                    indeterminate={someEnabledSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                  <Box
                    sx={{
                      fontSize: "0.85rem",
                      fontWeight: 600,
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                    onClick={() => handleSelectAll(!allEnabledSelected)}
                  >
                    {allEnabledSelected ? "Deselect All" : "Select All"}
                  </Box>
                </Box>
              )}
          </Box>

          <Box>
            <SelectComponentList
              key={`ee-${projectID}-${variantID}-${selectedZone}`}
              dataList={vsModifiedList}
              apiAction={labCar.get_vehicle_systems}
              searchKey={"name"}
              placeholder={"Search Vehicle System"}
              page={page}
              rowsPerPage={rowsPerPage}
              handlePageChange={(page) => setPage(page)}
              totalCount={count}
              isItemDisabled={(item) =>
                disabledVSComponents.includes(Number(item?.data?.id))
              }
              renderSelectIcon={(data) => {
                if (requireVehicleComponentSelection) {
                  return null;
                }
                const isDisabled = disabledVSComponents.includes(
                  Number(data.id),
                );
                return (
                  <Box>
                    <CustomCheckBoxWithoutFormControl
                      value={
                        (selectedVS || [])?.some(
                          (ele) => ele.id === data.id,
                        ) || false
                      }
                      disabled={isDisabled}
                      onChange={(e) => {
                        if (isDisabled) return;
                        if (e.target.checked) {
                          // Just add the VS — no VC API fetch needed when
                          // requireVehicleComponentSelection=false (user won't pick VCs)
                          setSelectedVS((prev) => [
                            ...(prev || []),
                            { ...data, vcData: [] },
                          ]);
                        } else {
                          setSelectedVS((prev) =>
                            (prev || []).filter((ele) => ele.id !== data.id),
                          );
                        }
                      }}
                    />
                  </Box>
                );
              }}
              renderCardActionIcons={(data) => {
                const isDisabled = disabledVSComponents.includes(
                  Number(data.id),
                );
                const hasVehicleComponents = data?.is_vc !== false;
                const disableVehicleComponentNavigation =
                  isDisabled || !hasVehicleComponents;
                return (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "center",
                      gap: 0.5,
                      pt: 2,
                      pr: 2,
                      ...(disableVehicleComponentNavigation && {
                        cursor: "pointer",
                        opacity: 0.5,
                      }),
                    }}
                    onClick={() => {
                      if (disableVehicleComponentNavigation) return;
                      setCurrentViewingVS(data);
                      setShowZone(vc);
                      if (selectedLegalMain?.lr_id) {
                        getDisabledVCListForLR({
                          lrId: selectedLegalMain.lr_id,
                          vsId: data.id,
                        });
                      } else {
                        setDisabledVCComponents([]);
                      }
                    }}
                  >
                    {!hasVehicleComponents && (
                      <CustomToolTip
                        type="component"
                        title={
                          "There is no vehicle Component"
                        }
                      >
                        <ErrorOutlineIcon
                          fontSize="small"
                          sx={{ color: "black" }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </CustomToolTip>
                    )}
                    <ChevronRightIcon
                      sx={{
                        color: disableVehicleComponentNavigation
                          ? "#9E9E9E"
                          : "inherit",
                      }}
                    />
                  </Box>
                );
              }}
              getDataOnSearch={(data, resp) => {
                if (resp.error) {
                  modifyVSData([]);
                } else {
                  modifyVSData(resp.data);
                  setCount(resp.total_count);
                }
              }}
              params={{
                project_id: Number(projectID),
                variant_id: Number(variantID),
                ...(isZonalProject && { zone_position: selectedZone }),
                status: "true",
                page_number: page,
                page_size: rowsPerPage,
              }}
              disabled={
                !projectID ||
                !variantID ||
                projectID === 0 ||
                variantID === 0
              }
            />
          </Box>
        </Box>
      )}

      {showZone === vc && (
        <Box>
          <Box sx={{ mb: 1 }}>
            <CommonBreadcrumbs
              color={"black"}
              items={[
                {
                  label: currentViewingVS?.name,
                  onClick: () => setShowZone(vs),
                },
                { label: "Vehicle Component" },
              ]}
            />
          </Box>
          <Box>
            <SelectComponentList
              key={`vc-${projectID}-${variantID}-${currentViewingVS?.id}`}
              dataList={vcModifiedList}
              apiAction={labCar.get_vehicle_components}
              searchKey={"name"}
              placeholder={"Search Vehicle Component"}
              page={page}
              rowsPerPage={rowsPerPage}
              handlePageChange={(page) => setPage(page)}
              totalCount={count}
              isItemDisabled={(item) =>
                disabledVCComponents.includes(Number(item?.data?.id))
              }
              renderSelectIcon={(data) => {
                if (!requireVehicleComponentSelection) {
                  return null;
                }
                const isDisabled = disabledVCComponents.includes(
                  Number(data.id),
                );
                const isChecked = (() => {
                  for (let vs of selectedVS) {
                    if (vs.id === data.pr_vs_id) {
                      return vs.vcData?.[0]?.id === data.id;
                    }
                  }
                  return false;
                })();
                return (
                  <Box sx={{ pl: 1.5, pt: 2 }}>
                    <Radio
                      name="vcComponent"
                      value={data.id}
                      checked={isChecked}
                      disabled={isDisabled}
                      onChange={(e) => {
                        e.stopPropagation();
                        if (isDisabled) return;
                        setSelectedVS((prev) => {
                          const existingVS = prev.find(
                            (vs) => vs.id === data.pr_vs_id,
                          );
                          if (existingVS) {
                            return prev.map((vs) =>
                              vs.id === data.pr_vs_id
                                ? { ...vs, vcData: [data] }
                                : vs,
                            );
                          }
                          return [
                            ...prev,
                            { ...currentViewingVS, vcData: [data] },
                          ];
                        });
                      }}
                      icon={unCheckedIconStyle({ disabled: isDisabled })}
                      checkedIcon={checkedIconStyle({ disabled: isDisabled })}
                      sx={{
                        p: 0,
                        "&:hover": { backgroundColor: "transparent" },
                      }}
                    />
                  </Box>
                );
              }}
              renderCardActionIcons={(data) => (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    pt: 2,
                    pr: 2,
                  }}
                />
              )}
              getDataOnSearch={(data, resp) => {
                if (resp.error) {
                  modifyVCData([]);
                } else {
                  modifyVCData(resp.data);
                  setCount(resp.total_count);
                }
              }}
              params={{
                project_id: Number(projectID),
                variant_id: Number(variantID),
                vs_id: currentViewingVS?.id,
                status: "true",
                page_number: page,
                page_size: rowsPerPage,
              }}
              disabled={!projectID || !variantID || !currentViewingVS?.id}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default SelectVehicleSystemDialog;
